import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import messageHandler from 'utils/messageHandler';
import { regenerateToken } from './loginSlice';

const realEstateSlice = createSlice({
  name: 'realEstate',
  initialState: {
    data: {},
    branchOfficesArray: [],
    realEstate: {},
    loadingBranchOffices: false,
    realEstateList: null,
    realEstatePropStats: null,
    realEstateProjStats: null
  },
  reducers: {
    setCurrentRealEstate: (state, { payload }) => {
      state.data = payload;
    },
    setRealEstatesBranchOffices: (state, { payload }) => {
      state.branchOfficesArray = payload;
    },
    setRealEstate: (state, { payload }) => {
      state.realEstate = payload;
    },
    setLoadingBranchOffices: (state, { payload }) => {
      state.loadingBranchOffices = payload;
    },
    setRealEstateList: (state, { payload }) => {
      state.realEstateList = payload;
    },
    setRealEstatePropStats: (state, { payload }) => {
      state.realEstatePropStats = payload;
    },
    setRealEstateProjStats: (state, { payload }) => {
      state.realEstateProjStats = payload;
    }
  }
});

export const {
  setCurrentRealEstate,
  setRealEstatesBranchOffices,
  setRealEstate,
  setLoadingBranchOffices,
  setRealEstateList,
  setRealEstatePropStats,
  setRealEstateProjStats
} = realEstateSlice.actions;

export const getRealEstateByUserId = userId => async dispatch => {
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_SERVER}/real-estate/user/${userId}`
    );

    dispatch(setRealEstate(data));
  } catch (e) {
    console.log(e);
  }
};

export const getRealEstateData = id => async dispatch => {
  dispatch(setLoadingBranchOffices(true));
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_SERVER}/real-estate/${id}`
    );

    dispatch(
      setCurrentRealEstate({
        id: data.id,
        name: data.name,
        logo: data.logo,
        socialNetworks: data.socialNetworks,
        matricula: data.matricula,
        email: data.email,
        userId: data.userId
      })
    );

    if (data.branchOffice.length > 1) {
      const central = data.branchOffice.find(office => office.isCentral);
      let offices = data.branchOffice.filter(office => !office.isCentral);
      offices = [central, ...offices];

      dispatch(setRealEstatesBranchOffices(offices));
    } else dispatch(setRealEstatesBranchOffices(data.branchOffice));
  } catch (e) {
    console.log(e);
  } finally {
    dispatch(setLoadingBranchOffices(false));
  }
};

export const emptyAllRealEstateData = () => async dispatch => {
  dispatch(setLoadingBranchOffices(true));
  try {
    dispatch(setCurrentRealEstate({}));
    dispatch(setRealEstatesBranchOffices([]));
    dispatch(setRealEstate({}));
    dispatch(setRealEstatePropStats(null));
    dispatch(setRealEstateProjStats(null));
  } catch (e) {
    console.log(e);
  } finally {
    dispatch(setLoadingBranchOffices(false));
  }
};

export const uploadRealEstateLogo = (id, formData) => async () => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_SERVER}/real-estate/logo/${id}`,
      formData
    );

    return response;
  } catch (e) {
    messageHandler('error', e.response?.data?.message);
  }
};

export const updateRealEstate =
  (id, { facebook, instagram, twitter, website, youtube, name, email }) =>
  async () => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER}/real-estate/${id}`,
        {
          facebook,
          instagram,
          twitter,
          website,
          youtube,
          name,
          email
        }
      );

      setCurrentRealEstate({
        id: response.data.body.id,
        name: response.data.body.name,
        logo: response.data.body.logo,
        socialNetworks: response.data.body.socialNetworks
      });

      messageHandler('success', response?.data?.message);

      return response;
    } catch (e) {
      messageHandler('error', e.response?.data?.message);
    }
  };

export const createRealEstate =
  ({ name, email, phoneNumber, address, userId }) =>
  async dispatch => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}/real-estate`,
        {
          name,
          email,
          phoneNumber,
          address,
          userId
        }
      );

      messageHandler('success', response?.data?.message);

      dispatch(regenerateToken());

      return response;
    } catch (e) {
      messageHandler('error', e.response?.data?.message);
    }
  };

export const getEstateByMatricula = matricula => async dispatch => {
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_SERVER}/real-estate/findByMatricula/${matricula}`
    );

    dispatch(setRealEstate(data));
  } catch (e) {
    console.log(e);
  }
};

export const getRealEstateList = () => async dispatch => {
  try {
    const url = `${process.env.REACT_APP_SERVER}/real-estate?offset=0&limit=9999`;
    const response = await axios.get(url);
    dispatch(
      setRealEstateList(
        response.data.result.map(re => {
          return { id: re.id, name: re.name, branchOffice: re.branchOffice };
        })
      )
    );
  } catch (e) {
    messageHandler('error', e.response?.data?.message);
  }
};

export const setRealEstateListNull = () => async dispatch =>
  dispatch(setRealEstateList(null));

export const getRealEstatePropStats = realEstateId => async dispatch => {
  try {
    const url = `${process.env.REACT_APP_SERVER}/real-estate/count-status-property/${realEstateId}`;
    const response = await axios.get(url);
    dispatch(setRealEstatePropStats(response.data));
  } catch (e) {
    messageHandler('error', e.response?.data?.message);
  }
};

export const getRealEstateProjectStats = realEstateId => async dispatch => {
  try {
    const url = `${process.env.REACT_APP_SERVER}/real-estate/count-status-project/${realEstateId}`;
    const response = await axios.get(url);
    dispatch(setRealEstateProjStats(response.data));
  } catch (e) {
    messageHandler('error', e.response?.data?.message);
  }
};

export const deleteRealEstate = realEstateId => async () => {
  try {
    const url = `${process.env.REACT_APP_SERVER}/real-estate/delete/${realEstateId}`;
    return await axios.patch(url);
  } catch (e) {
    return e.response;
  }
};

export default realEstateSlice.reducer;
